// Iframe Communication Utility - Overhauled Version
// Simplified, robust measurement using ResizeObserver on a single root element

import { getActivePinia } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { isLiteIntegration, resolveIntegrationMode } from '@/utils/integrationMode'

export interface IframeMessage {
  type: string;
  data?: Record<string, unknown>;
}

type MessageHandler = (data?: Record<string, unknown>) => void;

/** Body class set by DetailView on mobile — caps iframe height so fixed footers stay visible. */
export const DETAIL_MOBILE_VIEWPORT_CLASS = 'detail-mobile-viewport';

/** Body class set while the mobile betslip is open in iframe mode — caps host iframe height. */
export const BETSLIP_MOBILE_IFRAME_CLASS = 'betslip-mobile-iframe-open';

/**
 * TEMP: disable scroll-by postMessage until hosts (e.g. LSB casino-integration 755.js)
 * handle it. When false, wheel/touch use browser default iframe scroll chaining.
 */
const SCROLL_BY_FORWARDING_ENABLED = false;

export class IframeCommunication {
  private isInIframe: boolean;
  private resizeObserver: ResizeObserver | null = null;
  private lastHeight = 0;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners: Map<string, Set<MessageHandler>> = new Map();
  private parentReadyReceived = false;
  /** Default true: content-height embeds must scroll the host page, not the iframe doc. */
  private parentScrollEnabled = true;
  private wheelForwardHandler: ((event: WheelEvent) => void) | null = null;
  private touchStartHandler: ((event: TouchEvent) => void) | null = null;
  private touchProbeHandler: ((event: TouchEvent) => void) | null = null;
  private touchMoveHandler: ((event: TouchEvent) => void) | null = null;
  private touchEndHandler: ((event: TouchEvent) => void) | null = null;
  private touchStartY = 0;
  private touchStartX = 0;
  private touchTracking = false;
  private touchForwarding = false;
  private touchMoveListening = false;
  private pendingTouchDeltaX = 0;
  private pendingTouchDeltaY = 0;
  private touchForwardRaf: number | null = null;
  private keyboardRecoveryTimer: ReturnType<typeof setTimeout> | null = null;
  private lastVisualViewportHeight = 0;
  private focusOutRecoveryHandler: ((event: FocusEvent) => void) | null = null;
  private visualViewportRecoveryHandler: (() => void) | null = null;

  constructor() {
    this.isInIframe = window !== window.parent;
    this.setupMessageListener();
    if (this.isInIframe) {
      this.syncParentScrollModeFromUrl();
      this.setupWheelForwarding();
      this.setupTouchForwarding();
      this.setupKeyboardScrollRecovery();
    }
  }

  private syncParentScrollModeFromUrl(): void {
    try {
      const searchParams = new URLSearchParams(window.location.search)
      const hash = window.location.hash
      const hashIndex = hash.indexOf('?')
      const hashParams = hashIndex >= 0 ? new URLSearchParams(hash.slice(hashIndex + 1)) : null
      const scrollInside =
        searchParams.get('scroll-inside') || hashParams?.get('scroll-inside')
      const allowParent =
        searchParams.get('allow-parent-scroll') || hashParams?.get('allow-parent-scroll')
      if (scrollInside === 'true' || scrollInside === '1') {
        this.setParentScrollEnabled(false)
        return
      }
      if (allowParent === 'false' || allowParent === '0') {
        this.setParentScrollEnabled(false)
        return
      }
      // Default / allow-parent-scroll=true → parent scroll
      this.setParentScrollEnabled(true)
    } catch {
      this.setParentScrollEnabled(true)
    }
  }

  private setParentScrollEnabled(enabled: boolean): void {
    this.parentScrollEnabled = enabled
    const scrollInside = !enabled
    document.documentElement.classList.toggle('iframe-scroll-inside', scrollInside)
    document.body.classList.toggle('iframe-scroll-inside', scrollInside)
    this.setupWheelForwarding()
    this.setupTouchForwarding()
  }

  /** True when the iframe document itself has vertical overflow to scroll. */
  private documentCanScrollVertically(): boolean {
    const root = document.documentElement
    return root.scrollHeight > Math.ceil(window.innerHeight) + 1
  }

  private isDocumentScrollAtEdge(deltaY: number): boolean {
    const root = document.documentElement
    const scrollTop = root.scrollTop || document.body.scrollTop || 0
    const viewHeight = window.innerHeight || root.clientHeight
    const scrollHeight = root.scrollHeight
    const atTop = scrollTop <= 0
    const atBottom = scrollTop + viewHeight >= scrollHeight - 1
    return (deltaY < 0 && atTop) || (deltaY > 0 && atBottom)
  }

  private forwardScrollToParent(deltaX: number, deltaY: number): void {
    if (!SCROLL_BY_FORWARDING_ENABLED) return
    if (!deltaX && !deltaY) return
    window.parent.postMessage(
      {
        type: 'scroll-by',
        data: { deltaX, deltaY },
      },
      '*'
    )
  }

  /** Coalesce touch deltas to one postMessage per animation frame. */
  private queueTouchScrollToParent(deltaX: number, deltaY: number): void {
    this.pendingTouchDeltaX += deltaX
    this.pendingTouchDeltaY += deltaY
    if (this.touchForwardRaf != null) return
    this.touchForwardRaf = window.requestAnimationFrame(() => {
      this.touchForwardRaf = null
      const dx = this.pendingTouchDeltaX
      const dy = this.pendingTouchDeltaY
      this.pendingTouchDeltaX = 0
      this.pendingTouchDeltaY = 0
      this.forwardScrollToParent(dx, dy)
    })
  }

  /**
   * Chrome does not reliably chain wheel events from an iframe to the parent.
   * Forward to the host when the document fits (parent should scroll) or when
   * the document is already at a scroll edge. Never steal wheel while the
   * iframe still has internal content to scroll — that freezes mobile/desktop.
   */
  private setupWheelForwarding(): void {
    this.teardownWheelForwarding()
    if (!SCROLL_BY_FORWARDING_ENABLED || !this.isInIframe || !this.parentScrollEnabled) return

    this.wheelForwardHandler = (event: WheelEvent) => {
      if (event.ctrlKey) return // pinch-zoom
      if (this.isEventFromNestedScroller(event)) return

      const canScrollDoc = this.documentCanScrollVertically()
      if (canScrollDoc && !this.isDocumentScrollAtEdge(event.deltaY)) {
        // Native iframe document scroll handles this gesture.
        return
      }

      this.forwardScrollToParent(event.deltaX, event.deltaY)
      event.preventDefault()
    }

    window.addEventListener('wheel', this.wheelForwardHandler, { passive: false })
  }

  private teardownWheelForwarding(): void {
    if (this.wheelForwardHandler) {
      window.removeEventListener('wheel', this.wheelForwardHandler)
      this.wheelForwardHandler = null
    }
  }

  private isTouchHost(): boolean {
    try {
      return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0)
    } catch {
      return false
    }
  }

  private attachActiveTouchMoveListener(): void {
    if (this.touchMoveListening || !this.touchMoveHandler) return
    window.addEventListener('touchmove', this.touchMoveHandler, { passive: false })
    this.touchMoveListening = true
  }

  private detachActiveTouchMoveListener(): void {
    if (!this.touchMoveListening || !this.touchMoveHandler) return
    window.removeEventListener('touchmove', this.touchMoveHandler)
    this.touchMoveListening = false
  }

  /**
   * Mobile: forward vertical pans to the host so flings can reach page top/bottom.
   * Uses passive listeners until forwarding is needed, then attaches a temporary
   * non-passive touchmove only for that gesture (avoids always-on scroll jank).
   */
  private setupTouchForwarding(): void {
    this.teardownTouchForwarding()
    if (!SCROLL_BY_FORWARDING_ENABLED || !this.isInIframe || !this.parentScrollEnabled || !this.isTouchHost()) return

    this.touchStartHandler = (event: TouchEvent) => {
      if (event.touches.length !== 1) return
      if (this.isNativeFormControlTarget(event)) {
        this.touchTracking = false
        return
      }
      if (this.isTouchFromNestedScroller(event)) {
        this.touchTracking = false
        return
      }
      this.touchStartY = event.touches[0].clientY
      this.touchStartX = event.touches[0].clientX
      this.touchTracking = true
      this.touchForwarding = false
    }

    // Passive probe: decide whether this gesture should be forwarded.
    this.touchProbeHandler = (event: TouchEvent) => {
      if (!this.touchTracking || this.touchForwarding || event.touches.length !== 1) return

      const currentY = event.touches[0].clientY
      const currentX = event.touches[0].clientX
      const deltaY = this.touchStartY - currentY
      const deltaX = this.touchStartX - currentX

      if (Math.abs(deltaY) < 5 && Math.abs(deltaX) < 5) return

      // Horizontal gesture — leave to carousels / drawers.
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        this.touchTracking = false
        return
      }

      const canScrollDoc = this.documentCanScrollVertically()
      if (canScrollDoc && !this.isDocumentScrollAtEdge(deltaY)) {
        this.touchTracking = false
        return
      }

      this.touchForwarding = true
      this.touchStartY = currentY
      this.touchStartX = currentX
      this.attachActiveTouchMoveListener()
      this.queueTouchScrollToParent(deltaX, deltaY)
    }

    // Non-passive move: only attached while actively forwarding one gesture.
    this.touchMoveHandler = (event: TouchEvent) => {
      if (!this.touchForwarding || event.touches.length !== 1) return

      const currentY = event.touches[0].clientY
      const currentX = event.touches[0].clientX
      const deltaY = this.touchStartY - currentY
      const deltaX = this.touchStartX - currentX

      this.queueTouchScrollToParent(deltaX, deltaY)
      this.touchStartY = currentY
      this.touchStartX = currentX
      if (event.cancelable) event.preventDefault()
    }

    this.touchEndHandler = () => {
      this.touchTracking = false
      this.touchForwarding = false
      this.detachActiveTouchMoveListener()
      if (this.touchForwardRaf != null) {
        window.cancelAnimationFrame(this.touchForwardRaf)
        this.touchForwardRaf = null
        const dx = this.pendingTouchDeltaX
        const dy = this.pendingTouchDeltaY
        this.pendingTouchDeltaX = 0
        this.pendingTouchDeltaY = 0
        this.forwardScrollToParent(dx, dy)
      }
    }

    window.addEventListener('touchstart', this.touchStartHandler, { passive: true })
    window.addEventListener('touchmove', this.touchProbeHandler, { passive: true })
    window.addEventListener('touchend', this.touchEndHandler, { passive: true })
    window.addEventListener('touchcancel', this.touchEndHandler, { passive: true })
  }

  private teardownTouchForwarding(): void {
    this.detachActiveTouchMoveListener()
    if (this.touchForwardRaf != null) {
      window.cancelAnimationFrame(this.touchForwardRaf)
      this.touchForwardRaf = null
    }
    this.pendingTouchDeltaX = 0
    this.pendingTouchDeltaY = 0
    if (this.touchStartHandler) {
      window.removeEventListener('touchstart', this.touchStartHandler)
      this.touchStartHandler = null
    }
    if (this.touchProbeHandler) {
      window.removeEventListener('touchmove', this.touchProbeHandler)
      this.touchProbeHandler = null
    }
    if (this.touchMoveHandler) {
      this.touchMoveHandler = null
    }
    if (this.touchEndHandler) {
      window.removeEventListener('touchend', this.touchEndHandler)
      window.removeEventListener('touchcancel', this.touchEndHandler)
      this.touchEndHandler = null
    }
    this.touchTracking = false
    this.touchForwarding = false
  }

  private isEditableElement(el: EventTarget | null): boolean {
    if (!(el instanceof HTMLElement)) return false
    const tag = el.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
    return el.isContentEditable
  }

  /** Do not forward touch scroll when the user is opening a native control (e.g. date picker in iframe). */
  private isNativeFormControlTarget(event: Event): boolean {
    for (const node of this.pathNodesFromEvent(event)) {
      if (!(node instanceof Element)) continue
      if (node instanceof HTMLInputElement) {
        const type = (node.type || '').toLowerCase()
        if (type === 'date' || type === 'time' || type === 'datetime-local') return true
      }
      if (node instanceof HTMLSelectElement || node instanceof HTMLTextAreaElement) return true
      if (node.closest('.next-draws-filter')) return true
    }
    return false
  }

  private scheduleScrollportRecovery(delayMs: number): void {
    if (this.keyboardRecoveryTimer) clearTimeout(this.keyboardRecoveryTimer)
    this.keyboardRecoveryTimer = setTimeout(() => {
      this.recoverStuckScrollport()
    }, delayMs)
  }

  /**
   * Wake a stuck iframe/host scrollport after keyboard dismiss / viewport offset.
   * Kept light: no layout thrash loops, only runs on keyboard/viewport events.
   */
  private recoverStuckScrollport(): void {
    const active = document.activeElement
    if (this.isEditableElement(active)) {
      try {
        const editable = active as HTMLElement
        editable.blur()
      } catch {
        /* ignore */
      }
    }

    const visualViewport = window.visualViewport
    const scrollY = window.scrollY || document.documentElement.scrollTop || 0

    requestAnimationFrame(() => {
      const offsetTop = visualViewport?.offsetTop ?? 0
      if (offsetTop) {
        window.scrollTo(0, Math.max(0, scrollY + offsetTop))
      }
      this.sendImmediateHeightUpdate()
    })
  }

  private setupKeyboardScrollRecovery(): void {
    this.teardownKeyboardScrollRecovery()
    if (!this.isInIframe) return

    this.focusOutRecoveryHandler = () => {
      // Wait for focus to move to another field before treating as dismiss.
      if (this.keyboardRecoveryTimer) clearTimeout(this.keyboardRecoveryTimer)
      this.keyboardRecoveryTimer = setTimeout(() => {
        if (this.isEditableElement(document.activeElement)) return
        this.recoverStuckScrollport()
      }, 180)
    }
    document.addEventListener('focusout', this.focusOutRecoveryHandler, true)

    if (window.visualViewport) {
      this.lastVisualViewportHeight = window.visualViewport.height
      this.visualViewportRecoveryHandler = () => {
        const visualViewport = window.visualViewport
        if (!visualViewport) return

        const previousHeight = this.lastVisualViewportHeight
        const nextHeight = visualViewport.height
        this.lastVisualViewportHeight = nextHeight

        const keyboardLikelyClosed = nextHeight > previousHeight + 50
        const stuckOffset =
          visualViewport.offsetTop > 0 && !this.isEditableElement(document.activeElement)

        if (keyboardLikelyClosed || stuckOffset) {
          this.scheduleScrollportRecovery(50)
        }
      }
      window.visualViewport.addEventListener('resize', this.visualViewportRecoveryHandler)
      window.visualViewport.addEventListener('scroll', this.visualViewportRecoveryHandler)
    }
  }

  private teardownKeyboardScrollRecovery(): void {
    if (this.keyboardRecoveryTimer) {
      clearTimeout(this.keyboardRecoveryTimer)
      this.keyboardRecoveryTimer = null
    }
    if (this.focusOutRecoveryHandler) {
      document.removeEventListener('focusout', this.focusOutRecoveryHandler, true)
      this.focusOutRecoveryHandler = null
    }
    if (this.visualViewportRecoveryHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.visualViewportRecoveryHandler)
      window.visualViewport.removeEventListener('scroll', this.visualViewportRecoveryHandler)
      this.visualViewportRecoveryHandler = null
    }
  }

  private pathNodesFromEvent(event: Event): EventTarget[] {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : []
    if (path.length) return path
    return event.target instanceof Element ? [event.target] : []
  }

  /** Modals keep their own touch scrolling; horizontal carousels are detected by gesture axis. */
  private isTouchFromNestedScroller(event: TouchEvent): boolean {
    for (const node of this.pathNodesFromEvent(event)) {
      if (!(node instanceof Element)) continue
      if (node === document.documentElement || node === document.body) break
      if (node.classList?.contains('modal-overlay') || node.classList?.contains('modal-body')) {
        return true
      }
      const style = window.getComputedStyle(node)
      const oy = style.overflowY
      if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
        return true
      }
    }
    return false
  }

  /** Leave wheel to modals / lists that actually scroll inside the iframe. */
  private isEventFromNestedScroller(event: WheelEvent): boolean {
    for (const node of this.pathNodesFromEvent(event)) {
      if (!(node instanceof Element)) continue
      if (node === document.documentElement || node === document.body) break
      if (node.classList?.contains('modal-overlay') || node.classList?.contains('modal-body')) {
        return true
      }
      const style = window.getComputedStyle(node)
      const oy = style.overflowY
      if (oy !== 'auto' && oy !== 'scroll') continue
      if (node.scrollHeight > node.clientHeight + 1) {
        const atTop = node.scrollTop <= 0
        const atBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 1
        if ((event.deltaY < 0 && !atTop) || (event.deltaY > 0 && !atBottom)) {
          return true
        }
      }
    }
    return false
  }

  private shouldScrollParent(): boolean {
    if (!this.isInIframe) return false
    return this.parentScrollEnabled
  }

  public isRunningInIframe(): boolean {
    return this.isInIframe;
  }

  /** True after the host sent parent-ready (catalog may defer until this arrives). */
  public hasReceivedParentReady(): boolean {
    return this.parentReadyReceived;
  }

  /** Clone payload for postMessage / CustomEvent (strips Vue proxies). */
  private cloneMessageData(data?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (data === undefined) {
      return undefined;
    }
    try {
      return JSON.parse(JSON.stringify(data)) as Record<string, unknown>;
    } catch (e) {
      console.warn('Failed to clone message data:', e);
      return data;
    }
  }

  /**
   * Fires both `bitville:<name>` and `<name>` on this window (hosts may listen for either).
   */
  public emitIntegrationEvent(name: string, data?: Record<string, unknown>): void {
    const detail = this.cloneMessageData(data);
    const eventNames = [`bitville:${name}`, name];
    for (const eventName of eventNames) {
      try {
        window.dispatchEvent(new CustomEvent(eventName, { detail }));
      } catch (e) {
        console.warn(`Failed to dispatch ${eventName}:`, e);
      }
    }
  }

  /** Notify host + local listeners after a successful place-bet. */
  public notifyBetPlaced(data: Record<string, unknown>): void {
    this.emitIntegrationEvent('bet-placed', data);
    if (this.isInIframe) {
      const cloneableData = this.cloneMessageData(data);
      window.parent.postMessage({ type: 'bet-placed', data: cloneableData }, '*');
    }
  }

  public sendMessage(type: string, data?: Record<string, unknown>): void {
    if (!this.isInIframe) return;
    const cloneableData = this.cloneMessageData(data);
    window.parent.postMessage({ type, data: cloneableData }, '*');
  }

  /**
   * The single source of truth for height updates.
   * Sends both modern and legacy formats for backward compatibility.
   */
  public sendHeightUpdate(height?: number): void {
    if (!this.isInIframe) return;

    if (this.debounceTimer) clearTimeout(this.debounceTimer);

    this.debounceTimer = setTimeout(() => {
      const currentHeight = this.resolveReportedHeight(height);
      if (Math.abs(currentHeight - this.lastHeight) < 2) return;

      this.lastHeight = currentHeight;

      // 1. Modern Format (v3.0+)
      this.sendMessage('height-update', { height: currentHeight });

      // 2. Legacy: style-update format
      this.sendMessage('style-update', { style: { height: currentHeight } });

      // 3. Legacy: resize-event format
      this.sendMessage('resize-event', {
        events: { windowResize: [{ height: currentHeight }] }
      });

      // 4. Legacy: Stringified JSON packet (for extremely old integrations)
      try {
        window.parent.postMessage(JSON.stringify({
          type: 'height-update',
          data: { height: currentHeight },
          style: { height: currentHeight },
          events: { windowResize: [{ height: currentHeight }] }
        }), '*');
      } catch (e) { /* ignore */ }
    }, 60);
  }

  private isDetailMobileViewportLocked(): boolean {
    return document.body.classList.contains(DETAIL_MOBILE_VIEWPORT_CLASS);
  }

  private isMobileBetslipIframeOpen(): boolean {
    return document.body.classList.contains(BETSLIP_MOBILE_IFRAME_CLASS);
  }

  /**
   * While the mobile betslip is open inside an iframe embed, cap the reported height
   * to the visible viewport and lock background scroll (same pattern as BaseModal).
   */
  public setMobileBetslipIframeOpen(open: boolean): void {
    if (!this.isInIframe) return;

    document.body.classList.toggle(BETSLIP_MOBILE_IFRAME_CLASS, open);
    document.documentElement.classList.toggle('modal-open', open);
    document.body.classList.toggle('modal-open', open);

    if (open) {
      this.sendScrollToTop();
    }

    this.sendImmediateHeightUpdate();
  }

  private isAndroidDevice(): boolean {
    return /Android/i.test(navigator.userAgent);
  }

  /**
   * Estimate the visible mobile viewport without relying on innerHeight alone.
   * innerHeight reflects the current iframe size and can be wrong (too tall when
   * the host sized to document height, too short when capped at 720 on Android).
   */
  private estimateDetailMobileViewport(): number {
    const screenHeight = window.screen?.height ?? 740;
    const isAndroid = this.isAndroidDevice();

    if (isAndroid) {
      const availHeight = window.screen?.availHeight;
      if (availHeight && availHeight >= 320) {
        return Math.ceil(availHeight);
      }
      return Math.max(520, Math.round(screenHeight * 0.88));
    }

    return Math.min(720, Math.max(520, Math.round(screenHeight * 0.72)));
  }

  /**
   * When the detail page pins a fixed footer, the host must not size the iframe to
   * full document height. Report a mobile viewport cap instead and scroll inside.
   */
  private getDetailMobileViewportCap(): number {
    const estimated = this.estimateDetailMobileViewport();
    const inner =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      document.body.clientHeight ||
      0;

    if (inner < 320) {
      return estimated;
    }

    // Host iframe still sized to full document — do not report that inflated height.
    if (inner > estimated * 1.15) {
      return estimated;
    }

    // Android: iframe shorter than visible viewport leaves a gap below the fixed footer.
    if (this.isAndroidDevice() && inner < estimated - 20) {
      return estimated;
    }

    return Math.ceil(inner);
  }

  /** Used by DetailView to lock document height to the same value sent to the host. */
  public getDetailMobileViewportHeight(): number {
    return this.getDetailMobileViewportCap();
  }

  private measureUncappedHeight(): number {
    const root = document.getElementById('iframe-root') || document.getElementById('app');
    if (!root) return document.body.offsetHeight;
    return Math.ceil(root.getBoundingClientRect().height);
  }

  private resolveReportedHeight(explicitHeight?: number): number {
    if (this.isDetailMobileViewportLocked() || this.isMobileBetslipIframeOpen()) {
      return this.getDetailMobileViewportCap();
    }
    if (explicitHeight != null && explicitHeight > 0) {
      return Math.ceil(explicitHeight);
    }
    return this.measureUncappedHeight();
  }

  public measureHeight(): number {
    return this.resolveReportedHeight();
  }

  /**
   * Sends a height update immediately, bypassing the debounce.
   * Useful for layout shifts that require an instant response (like "Load More").
   */
  public sendImmediateHeightUpdate(): void {
    if (!this.isInIframe) return;
    const currentHeight = this.resolveReportedHeight();
    this.lastHeight = currentHeight;
    
    this.sendMessage('height-update', { height: currentHeight });
    this.sendMessage('style-update', { style: { height: currentHeight } });
    this.sendMessage('resize-event', {
      events: { windowResize: [{ height: currentHeight }] }
    });
  }

  public sendReady(): void {
    if (!this.isInIframe) return;
    const integrationMode = resolveIntegrationMode()
    this.sendMessage('iframe-ready', { 
      url: window.location.href,
      height: this.measureHeight(),
      integration: integrationMode ?? 'full',
      isLiteIntegration: isLiteIntegration(integrationMode),
    });
    this.sendHeightUpdate();
  }

  /**
   * Request login from the host.
   * Sends the modern iframe postMessage plus CustomEvents for host pages that
   * wire login from same-origin frame/window listeners.
   */
  public sendLoginRequest(reason?: string): void {
    const data: Record<string, unknown> = {}
    if (reason !== undefined) {
      data.reason = reason
    }

    try {
      window.dispatchEvent(
        new CustomEvent('bitville:login-request', {
          bubbles: true,
          detail: { ...data },
        })
      )
    } catch (e) {
      console.warn('[Bitville] bitville:login-request dispatch on frame window failed:', e)
    }

    if (this.isInIframe && window.parent !== window) {
      try {
        window.parent.dispatchEvent(
          new CustomEvent('bitville:login-request', {
            bubbles: true,
            detail: { ...data },
          })
        )
      } catch {
        // Same-origin parent only; cross-origin throws SecurityError — expected.
      }
    }

    this.sendMessage('login-request', data);
  }

  public sendScrollToTop(): void {
    if (!this.isInIframe) return;
    if (!this.shouldScrollParent()) return;
    this.sendMessage('scroll-request', { position: 'point', y: 0 });
    window.parent.postMessage({ scroll: { position: 'point', y: 0 } }, '*');
  }

  public sendRouteChange(route: string, title?: string): void {
    this.sendMessage('route-changed', { route, title });
    if (this.shouldScrollParent()) {
      this.sendMessage('scroll-request', { position: 'point', y: 0 });
      window.parent.postMessage({ scroll: { position: 'point', y: 0 } }, '*');
    }
    
    // Force a few updates as Vue swaps components
    [50, 200, 500].forEach(delay => {
      setTimeout(() => this.sendHeightUpdate(), delay);
    });
  }

  /**
   * Modern measurement using ResizeObserver.
   * We only watch the main container.
   */
  public startObserving(elementId = 'app'): void {
    if (!this.isInIframe || !window.ResizeObserver) return;

    this.stopObserving();

    const target = document.getElementById(elementId);
    if (!target) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.sendHeightUpdate();
    });

    this.resizeObserver.observe(target);
  }

  public stopObserving(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  /**
   * Accept object or JSON string (some hosts stringify postMessage payloads).
   */
  private parseMessagePayload(raw: unknown): Record<string, unknown> | null {
    if (raw == null) return null
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw) as unknown
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          return parsed as Record<string, unknown>
        }
        return null
      } catch {
        return null
      }
    }
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return raw as Record<string, unknown>
    }
    return null
  }

  /**
   * Color mode may be nested under `data` or set on the message root.
   */
  private mergeColorModeFields(
    root: Record<string, unknown>,
    nested?: Record<string, unknown>
  ): Record<string, unknown> {
    const n = nested && typeof nested === 'object' ? nested : {}
    const mode =
      n.mode ??
      n.colorMode ??
      root.mode ??
      root.colorMode ??
      root.theme
    return { ...n, mode }
  }

  private setupMessageListener(): void {
    window.addEventListener('message', (event) => {
      const root = this.parseMessagePayload(event.data)
      if (!root) return

      const type = typeof root.type === 'string' ? root.type : ''
      let nested = root.data
      if (typeof nested === 'string') {
        const inner = this.parseMessagePayload(nested)
        nested = inner ?? undefined
      }
      const data =
        nested && typeof nested === 'object' && !Array.isArray(nested)
          ? (nested as Record<string, unknown>)
          : undefined

      switch (type) {
        case 'parent-ready':
          this.handleParentReady(data);
          break;
        case 'bv-color-mode':
        case 'bitville-color-mode':
        case 'bv-set-mode':
          this.applyParentColorMode(this.mergeColorModeFields(root, data));
          break;
        case 'resize-request':
          this.sendHeightUpdate();
          break;
        case 'navigate':
          this.emit('navigate', data);
          break;
        case 'promotion-available':
          this.emit('promotion-available', data);
          break;
        case 'promotion-cleared':
          this.emit('promotion-cleared', data);
          break;
        case 'place-promotion':
          this.emit('place-promotion', data);
          break;
        default:
          this.emit(type, data);
      }
    });
  }

  private handleParentReady(data?: Record<string, unknown>, piniaAttempt = 0): void {
    if (data?.config && typeof data.config === 'object') {
      const config = data.config as Record<string, unknown>;

      // Parent-ready can arrive before app.use(pinia); retry briefly like color mode.
      if (!getActivePinia()) {
        if (piniaAttempt < 40) {
          window.setTimeout(() => this.handleParentReady(data, piniaAttempt + 1), 50)
        }
        return
      }

      // Apply tenant account before emit so early / in-flight catalog requests get auth-account.
      useAuthStore().applyParentConfig(config);

      // Immediate API Base URL update
      if (config.GAME_SERVER_URL && typeof config.GAME_SERVER_URL === 'string') {
        import('@/api').then(({ setApiBaseUrl }) => {
          setApiBaseUrl(config.GAME_SERVER_URL as string);
        });
      }

      const colorMode = config.colorMode ?? config.themeMode;
      if (
        colorMode === 'light' ||
        colorMode === 'dark' ||
        colorMode === 'system' ||
        colorMode === 'auto'
      ) {
        this.applyParentColorMode({
          mode: colorMode === 'auto' ? 'system' : colorMode,
        });
      }

      const scrollInside =
        config.scrollInsideIframe === true ||
        config.scrollInsideIframe === 'true' ||
        config.scrollInsideIframe === 1
      const allowParent =
        config.allowParentScroll === true ||
        config.allowParentScroll === 'true' ||
        config.allowParentScroll === 1
      const denyParent =
        config.allowParentScroll === false ||
        config.allowParentScroll === 'false' ||
        config.allowParentScroll === 0
      if (scrollInside || denyParent) {
        this.setParentScrollEnabled(false)
      } else if (allowParent || config.allowParentScroll === undefined) {
        // Default parent-scroll unless host explicitly opted into scroll-inside.
        this.setParentScrollEnabled(true)
      }
    }
    this.parentReadyReceived = true;
    this.emit('parent-ready', data);
  }

  /**
   * Apply light/dark from parent (postMessage). Canonical: { type: 'bv-color-mode', data: { mode: 'light' | 'dark' } }.
   * If the user used the in-app toggle, messages are ignored unless `data.force === true` (then override is cleared).
   * Parent can also pass config.colorMode / config.themeMode on parent-ready.
   */
  private applyParentColorMode(
    data?: Record<string, unknown>,
    piniaAttempt = 0
  ): void {
    if (!this.isInIframe) return;

    const raw = data?.mode ?? data?.colorMode;
    const normalized =
      typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (
      normalized !== 'light' &&
      normalized !== 'dark' &&
      normalized !== 'system' &&
      normalized !== 'auto'
    ) {
      return;
    }

    if (!getActivePinia()) {
      if (piniaAttempt < 40) {
        window.setTimeout(
          () => this.applyParentColorMode(data, piniaAttempt + 1),
          50
        );
      }
      return;
    }

    const resolved = normalized === 'auto' ? 'system' : normalized;

    void import('@/stores/theme').then(({ useThemeStore }) => {
      try {
        const store = useThemeStore();
        if (store.lockedColorMode) {
          return;
        }
        const forced =
          data &&
          typeof data === 'object' &&
          'force' in data &&
          (data as { force?: unknown }).force === true;
        if (store.userOverrodeColorMode && !forced) {
          return;
        }
        if (forced) {
          store.clearUserColorModeOverride();
        }
        if (resolved === 'system') {
          store.enableSystemColorScheme();
          void import('@/utils/systemColorSchemeWatcher').then(
            ({ syncSystemColorSchemeListener }) => {
              syncSystemColorSchemeListener();
            }
          );
          void import('@/utils/syncColorModeToUrl').then(({ syncColorModeToUrl }) =>
            syncColorModeToUrl('system')
          );
        } else {
          store.setMode(resolved);
          void import('@/utils/syncColorModeToUrl').then(({ syncColorModeToUrl }) =>
            syncColorModeToUrl(resolved)
          );
        }
      } catch (e) {
        console.warn('Parent color mode: could not apply theme', e);
      }
    });
  }

  public on(type: string, handler: MessageHandler): () => void {
    let handlers = this.listeners.get(type);
    if (!handlers) {
      handlers = new Set();
      this.listeners.set(type, handlers);
    }
    handlers.add(handler);
    return () => this.off(type, handler);
  }

  public off(type: string, handler: MessageHandler): void {
    this.listeners.get(type)?.delete(handler);
  }

  private emit(type: string, data?: Record<string, unknown>): void {
    this.listeners.get(type)?.forEach(h => h(data));
  }

  public destroy(): void {
    this.stopObserving();
    this.teardownWheelForwarding();
    this.teardownTouchForwarding();
    this.teardownKeyboardScrollRecovery();
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }
}

export const iframeCommunication = new IframeCommunication();
